<script setup>
import { Link, router, usePage } from '@inertiajs/vue3'
import { computed } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiTable from '@/Components/UI/UiTable.vue'
import EmptyState from '@/Components/UI/EmptyState.vue'
import { useI18n } from 'vue-i18n'

defineOptions({ layout: AppLayout })

defineProps({
  sources: { type: Array, default: () => [] }
})

const { t } = useI18n()
const page    = usePage()
const isAdmin = computed(() => page.props.auth?.user?.role === 'admin')

const columns = computed(() => [
  { key: 'name',                label: t('suppliers.cols.name') },
  { key: 'active_sub_rentals',  label: t('suppliers.cols.active'),             numeric: true, align: 'right' },
  { key: 'vehicles_all_time',   label: t('suppliers.cols.vehicles'),           numeric: true, align: 'right' },
  { key: 'outstanding_balance', label: t('suppliers.cols.outstanding'),        numeric: true, align: 'right' },
  { key: 'deposit_at_supplier', label: t('suppliers.cols.depositsAtSupplier'),  numeric: true, align: 'right' },
  { key: 'contact',             label: t('suppliers.cols.contact'),            hideBelow: 'md' }
])

const rowHref = (s) => `/vehicle_sources/${s.id}`
const onRowClick = (s) => router.visit(rowHref(s))
</script>

<template>
  <PageShell
    :title="t('suppliers.index.title')"
    back-href="/"
    :back-label="t('common.back')"
  >
    <template #actions>
      <Link
        v-if="isAdmin"
        href="/vehicle_sources/new"
      >
        <UiButton
          variant="primary"
          icon-left="plus"
        >
          {{ t('suppliers.index.new') }}
        </UiButton>
      </Link>
    </template>

    <EmptyState
      v-if="sources.length === 0"
      type="first-use"
      :title="t('suppliers.index.empty')"
    />

    <UiTable
      v-else
      :rows="sources"
      :columns="columns"
      :row-href="rowHref"
      @row-click="onRowClick"
    >
      <template #cell-name="{ row }">
        <span class="font-medium text-[color:var(--ink)]">{{ row.name }}</span>
      </template>
      <template #cell-contact="{ row }">
        <span class="text-sm text-[color:var(--inkSoft)]">
          {{ row.contact_phone || row.contact_email || '—' }}
        </span>
      </template>
      <template #cell-outstanding_balance="{ row }">
        <span
          class="fw-value tabular-nums"
          :class="row.outstanding_balance_cents > 0 ? 'text-[color:var(--warn)]' : 'text-[color:var(--ink)]'"
        >
          {{ row.outstanding_balance }}
        </span>
      </template>
      <template #cell-deposit_at_supplier="{ row }">
        <span
          class="fw-value tabular-nums"
          :class="row.deposit_at_supplier_cents > 0 ? 'text-[color:var(--accent)]' : 'text-[color:var(--muted)]'"
        >
          {{ row.deposit_at_supplier_cents > 0 ? row.deposit_at_supplier : '—' }}
        </span>
      </template>
      <template #cell-active_sub_rentals="{ row }">
        <span class="fw-value tabular-nums">{{ row.active_sub_rentals }}</span>
      </template>
      <template #cell-vehicles_all_time="{ row }">
        <span class="fw-value tabular-nums">{{ row.vehicles_all_time }}</span>
      </template>
    </UiTable>
  </PageShell>
</template>
